import { useEffect, useRef } from 'react';
import axios from 'axios';
import {
  LEVEL_CODE,
  LEVEL_ORDER,
  baseLevel,
  currentEscalationLevel,
  escalationStepsCrossed,
} from '../utils/escalation';

const SMS_GATEWAY_URL = 'https://smsgateway.pitb.gov.pk/api/send_sms';
const STATUS_NOTIFY_NUMBER = '03379926560';
const POLL_INTERVAL_MS = 60 * 1000;
const RECENT_EVENT_WINDOW_MS = 2 * 60 * 60 * 1000;
const STORAGE_PREFIX = 'cms-escalation-sms:';

const designationMatchesLevel = (designation, level) => {
  const value = String(designation || '').toLowerCase();
  if (level === 'Chief') {
    return value.includes('chief') || /\bccf\b/.test(value) || value.includes('zone');
  }
  if (level === 'Conservator') {
    return !value.includes('chief') &&
      (value.includes('conservator') || /\bcf\b/.test(value) || value.includes('circle'));
  }
  return value.includes('dfo') || /\bdcf\b/.test(value) || value.includes('division');
};

const findOfficerForLevel = (complaint, level) =>
  (complaint.persons_in_loop || []).find(
    (person) => person?.phone_no && designationMatchesLevel(person.designation, level)
  );

const sendSms = (phone, message) => axios.post(SMS_GATEWAY_URL, null, {
  params: {
    sec_key: process.env.REACT_APP_PITB_KEY,
    sms_text: message,
    phone_no: phone,
    sms_language: 'english',
  },
});

// Escalation is advanced by the server clock, while SMS delivery remains in
// the frontend like the other complaint stages. Only control-room roles run
// this watcher. A local marker prevents repeat messages for the same complaint
// and DCF/CF/CCF level when the page refreshes or the poll runs again.
const EscalationSmsNotifier = ({ user }) => {
  const scanInProgress = useRef(false);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  useEffect(() => {
    const role = String(user?.role || '').toLowerCase();
    if (!['admin', 'cro', 'cro_lead'].includes(role)) return undefined;

    const scanEscalations = async () => {
      if (scanInProgress.current) return;
      scanInProgress.current = true;

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        let pageNo = 1;
        let totalPages = 1;
        const complaints = [];

        do {
          const response = await axios.post(
            `${API_BASE_URL}/backend/complaints/get-complaints-by-status-name`,
            { status: 'Escalated' },
            {
              params: { perPage: 100, pageNo },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          complaints.push(...(response.data?.data || []));
          totalPages = Math.min(Number(response.data?.totalPages || 1), 50);
          pageNo += 1;
        } while (pageNo <= totalPages);

        for (const complaint of complaints) {
          const level = currentEscalationLevel(complaint);
          if (!level) continue;

          const markerKey = `${STORAGE_PREFIX}${complaint._id}:${level}`;
          if (localStorage.getItem(markerKey)) continue;

          const step = escalationStepsCrossed(complaint).find((item) => item.level === level);
          const eventAt = step?.at ? new Date(step.at).getTime() : NaN;
          const isRecent = Number.isFinite(eventAt) && Date.now() - eventAt <= RECENT_EVENT_WINDOW_MS;

          // Do not send a flood for historic escalations on the first deployment.
          // Mark them as seen; a newly crossed ladder level gets its own marker.
          if (!isRecent) {
            localStorage.setItem(markerKey, 'historic');
            continue;
          }

          const complaintNo = complaint.complaint_number || '';
          const nature = complaint.complaint_nature_id?.name || complaint.complaint_nature || '';
          const division = complaint.complaint_forest_division_id?.name ||
            complaint.complaint_district_id?.name || '';
          const designation = LEVEL_CODE[level] || level;
          const responsibleOfficer = findOfficerForLevel(complaint, level);

          const baseIndex = Math.max(0, LEVEL_ORDER.indexOf(baseLevel(complaint)));
          const currentIndex = Math.max(baseIndex, LEVEL_ORDER.indexOf(level));
          const missedLevel = currentIndex > baseIndex
            ? LEVEL_ORDER[currentIndex - 1]
            : LEVEL_ORDER[baseIndex];
          const missedOfficer = findOfficerForLevel(complaint, missedLevel);

          const sends = [];
          if (responsibleOfficer?.phone_no) {
            sends.push(sendSms(
              responsibleOfficer.phone_no,
              `Dear Sir, URGENT: Complaint ${complaintNo} ('${nature}', ${division}) has been escalated to ${designation} level after inactivity. Immediate action is required.`
            ));
          }
          if (missedOfficer?.phone_no) {
            sends.push(sendSms(
              missedOfficer.phone_no,
              `Dear Sir, Reminder/Escalation: Complaint ${complaintNo} assigned to you was not closed in time and has been escalated to a higher level.`
            ));
          }

          const previousLabel = currentIndex > baseIndex
            ? LEVEL_CODE[LEVEL_ORDER[currentIndex - 1]]
            : 'In Progress';
          sends.push(sendSms(
            STATUS_NOTIFY_NUMBER,
            `Dear Sir, complaint ${complaintNo} ('${nature}') status has changed from ${previousLabel} to Escalated at ${designation} level.`
          ));

          await Promise.all(sends);
          localStorage.setItem(markerKey, new Date().toISOString());
        }
      } catch (error) {
        console.error('Error sending escalation SMS:', error);
      } finally {
        scanInProgress.current = false;
      }
    };

    scanEscalations();
    const intervalId = window.setInterval(scanEscalations, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [API_BASE_URL, user?.role]);

  return null;
};

export default EscalationSmsNotifier;
